import { vec3, quat, mat4 } from "_src/libs/gl-matrix";
import { decomposeMat4 } from "_src/engine/math";

export default class Object3D {
  constructor() {

    this.trans = vec3.create();
    this.scale = vec3.fromValues(1,1,1);
    this.quat  = quat.create();

    this._children = [];
    this._parent = null;

    this._needsModelMatUpdate = false;
    this._needsLocalMatUpdate = false;
    this._needsTraverseChildren = false;

    this.modelMatrix = mat4.create();
    this.localMatrix = mat4.create();
    this.isDrawable = false;

  }

  add(child) {

    child.setParent(this);

    this._children.push(child);

    const scene = this.getRootNode();

    if (!scene) {
      return;
    }

    if (scene.batcher) {
      scene.batcher.addObject(child);
    }

  }

  remove(obj) {

    const ind = this._children.indexOf(obj);

    if (ind != -1) {

      this._children.splice(ind, 1);

      const scene = this.getRootNode();

      if (!scene) {
        return;
      }

      scene.batcher.removeObject(obj);

    }

  }

  setParent(parent) {
    this._parent = parent;
  }

  getParent() {
    return this._parent;
  }

  getRootNode() {

    let parent = this._parent;

    if (!parent) {
      return null;
    }

    while(parent._parent) {
      parent = parent._parent;
    }

    return parent;

  }

  getTranslation(destVec) {
    if (!destVec) {
      return vec3.clone(this.trans);
    }
    return vec3.copy(destVec, this.trans);
  }

  getRotation(destQuat) {
    if (!destQuat) {
      return quat.clone(this.quat);
    }
    return quat.copy(destQuat, this.quat);
  }

  getScale(destVec) {
    if (!destVec) {
      return vec3.clone(this.scale);
    }
    return vec3.copy(destVec, this.scale);
  }

  setTranslation(x, y, z) {
    vec3.set(this.trans, x, y, z);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  copyTranslation(position) {
    vec3.copy(this.trans, position);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  setRotation(x, y, z, w) {
    quat.set(this.quat, x, y, z, w);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  copyRotation(quaternion) {
    quat.copy(this.quat, quaternion);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  setScale(x, y, z) {
    vec3.set(this.scale, x, y, z);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  copyScale(scale) {
    vec3.copy(this.scale, scale);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  translate(trans) {
    vec3.add(this.trans, this.trans, trans);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  rotate(quaternion) {
    quat.multiply(this.quat, quaternion, this.quat);
    this._needsLocalMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  setMatrix(matrix) {
    mat4.copy(this.localMatrix, matrix);
    decomposeMat4(matrix, this.trans, this.quat, this.scale);

    this._needsModelMatUpdate = true;
    for (let node = this._parent; node; node = node._parent) {
      node._needsTraverseChildren = true;
    }
  }

  getMatrix(dest) {
    return mat4.copy(dest, this.localMatrix);
  }

  updateMatrix() {

    if (this._needsLocalMatUpdate) {
      mat4.fromRotationTranslationScale(this.localMatrix, this.quat, this.trans, this.scale);
      this._needsModelMatUpdate = true;
      this._needsLocalMatUpdate = false;
    }

    if (this._needsModelMatUpdate) {
      this._updateModelMatrix();
      this._needsTraverseChildren = true;
      this._needsModelMatUpdate = false;
    }

    if (this._needsTraverseChildren) {
      for (const child of this._children) {
        child.updateMatrix();
      }
      this._needsTraverseChildren = false;
    }
  }

  _updateModelMatrix() {
    mat4.copy(this.modelMatrix, this.localMatrix);

    if (this._parent) {
      mat4.multiply(this.modelMatrix, this._parent.modelMatrix, this.modelMatrix);
    }

    for (const child of this._children) {
      child._needsModelMatUpdate = true;
    }
  }

  getChildren() {
    return this._children;
  }

  getAllNestedChildren() {
    let allChildren = this._children.slice();
    for (const child of this._children) {
      allChildren = allChildren.concat(child.getAllNestedChildren());
    }

    return allChildren;
  }

  checkNeedsMatUpdate() {
    return this._needsLocalMatUpdate || this._needsModelMatUpdate;
  }

  //interface like methods
  setSelectionEnabled() {}
  setColorMode() {}
  setWireframeEnabled() {}
  setTransparencyEnabled() {}
}
